import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardCheck, Copy, FileText, ChevronDown, ChevronUp } from "lucide-react";
import type { TotalsData } from "@/components/customizable-widget-panel";

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totals: TotalsData;
  effectiveTotalLeads: number;
  accountName: string;
  dateLabel: string;
  dashboardMode: string;
  previewEnabled?: boolean;
}

// ─── Metric definition ───

interface ReportMetric {
  id: string;
  label: string;
  getValue: (t: TotalsData, leads: number) => string;
  modes: string[];
}

function fmtCurrency(value: number): string {
  if (value === 0) return "0";
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtNum(value: number): string {
  return new Intl.NumberFormat("pl-PL").format(value);
}

function valOrNoData(value: number, formatter: (v: number) => string, noData: string): string {
  return value > 0 ? formatter(value) : noData;
}

function buildAllMetrics(t: TFunction, noData: string): ReportMetric[] {
  const tm = (k: string) => t(`metrics.${k}`) as string;
  return [
    { id: "spend", label: tm("spend"), getValue: (tot) => fmtCurrency(tot.spend), modes: ["all"] },
    {
      id: "leads",
      label: tm("leads"),
      getValue: (_t, leads) => fmtNum(leads),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "costPerLead",
      label: tm("costPerLead"),
      getValue: (tot) => valOrNoData(tot.costPerLead, fmtCurrency, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "bookedAppointments",
      label: tm("bookedAppointments"),
      getValue: (tot) => valOrNoData(tot.bookedAppointments, fmtNum, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "costPerBooking",
      label: tm("costPerBooking"),
      getValue: (tot) =>
        tot.bookedAppointments > 0 ? fmtCurrency(tot.spend / tot.bookedAppointments) : noData,
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "showedAppointments",
      label: tm("showedAppointments"),
      getValue: (tot) => valOrNoData(tot.showedAppointments, fmtNum, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "noShow",
      label: tm("noShow"),
      getValue: (tot) => fmtNum(tot.noShowAppointments),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "showRate",
      label: tm("showRate"),
      getValue: (tot) => (tot.showRate > 0 ? `${tot.showRate.toFixed(2)}%` : noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "sales",
      label: tm("sales"),
      getValue: (tot) => valOrNoData(tot.sales, fmtNum, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "roas",
      label: tm("roas"),
      getValue: (tot) => (tot.revenueRoas > 0 ? `${tot.revenueRoas.toFixed(2)}x` : noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "ltv",
      label: tm("ltv"),
      getValue: (tot) => valOrNoData(tot.ltv, fmtCurrency, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "ltvCac",
      label: tm("ltvCac"),
      getValue: (tot) => (tot.ltvCac > 0 ? `${tot.ltvCac.toFixed(2)}x` : noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "revenue",
      label: tm("revenue"),
      getValue: (tot) => valOrNoData(tot.revenue, fmtCurrency, noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    { id: "cpm", label: tm("cpm"), getValue: (tot) => fmtCurrency(tot.cpm), modes: ["all"] },
    { id: "cpc", label: tm("cpc"), getValue: (tot) => fmtCurrency(tot.cpc), modes: ["all"] },
    { id: "clicks", label: tm("clicks"), getValue: (tot) => fmtNum(tot.clicks), modes: ["all"] },
    {
      id: "impressions",
      label: tm("impressions"),
      getValue: (tot) => fmtNum(tot.impressions),
      modes: ["all"],
    },
    {
      id: "landingPageViews",
      label: tm("landingPageViews"),
      getValue: (tot) => fmtNum(tot.landingPageViews),
      modes: ["lead_gen", "lead_gen_advanced", "saas"],
    },
    {
      id: "crLeadToBooking",
      label: tm("crLeadToBooking"),
      getValue: (tot) => (tot.crLeadToBooking > 0 ? `${tot.crLeadToBooking.toFixed(2)}%` : noData),
      modes: ["lead_gen", "lead_gen_advanced"],
    },
    {
      id: "crClickToLead",
      label: tm("crClickToLead"),
      getValue: (tot) => (tot.crClickToLead > 0 ? `${tot.crClickToLead.toFixed(2)}%` : noData),
      modes: ["lead_gen", "lead_gen_advanced", "saas"],
    },
    {
      id: "ctr",
      label: tm("ctr"),
      getValue: (tot) => (tot.ctr > 0 ? `${tot.ctr.toFixed(2)}%` : noData),
      modes: ["all"],
    },
    {
      id: "purchaseValue",
      label: tm("purchaseValue"),
      getValue: (tot) => valOrNoData(tot.purchaseValue, fmtCurrency, noData),
      modes: ["ecommerce"],
    },
    {
      id: "roasEcom",
      label: tm("roasEcom"),
      getValue: (tot) => (tot.roas > 0 ? `${tot.roas.toFixed(2)}x` : noData),
      modes: ["ecommerce"],
    },
    {
      id: "purchases",
      label: tm("purchases"),
      getValue: (tot) => valOrNoData(tot.purchases, fmtNum, noData),
      modes: ["ecommerce"],
    },
    {
      id: "cpa",
      label: tm("cpa"),
      getValue: (tot) => (tot.purchases > 0 ? fmtCurrency(tot.spend / tot.purchases) : noData),
      modes: ["ecommerce"],
    },
    {
      id: "aov",
      label: tm("aov"),
      getValue: (tot) =>
        tot.purchases > 0 ? fmtCurrency(tot.purchaseValue / tot.purchases) : noData,
      modes: ["ecommerce"],
    },
    { id: "trials", label: tm("trials"), getValue: (tot) => fmtNum(tot.trials), modes: ["saas"] },
    {
      id: "costPerTrial",
      label: tm("costPerTrial"),
      getValue: (tot) => valOrNoData(tot.costPerTrial, fmtCurrency, noData),
      modes: ["saas"],
    },
    {
      id: "subscriptions",
      label: tm("subscriptions"),
      getValue: (tot) => valOrNoData(tot.subscriptions, fmtNum, noData),
      modes: ["saas"],
    },
    {
      id: "costPerSubscription",
      label: tm("costPerSubscription"),
      getValue: (tot) => valOrNoData(tot.costPerSubscription, fmtCurrency, noData),
      modes: ["saas"],
    },
    {
      id: "crTrialToSub",
      label: tm("crTrialToSub"),
      getValue: (tot) =>
        tot.trials > 0 && tot.subscriptions > 0
          ? `${((tot.subscriptions / tot.trials) * 100).toFixed(2)}%`
          : noData,
      modes: ["saas"],
    },
  ];
}

// ─── Default selections per mode (from user's schema) ───

const DEFAULT_LEAD_GEN: string[] = [
  "spend",
  "leads",
  "costPerLead",
  "bookedAppointments",
  "costPerBooking",
  "sales",
  "roas",
];

const DEFAULT_ECOMMERCE: string[] = [
  "spend",
  "purchaseValue",
  "roasEcom",
  "purchases",
  "cpa",
  "aov",
  "cpm",
  "cpc",
  "clicks",
];

const DEFAULT_SAAS: string[] = [
  "spend",
  "trials",
  "costPerTrial",
  "subscriptions",
  "costPerSubscription",
  "crTrialToSub",
  "cpm",
  "cpc",
];

const DEFAULTS_BY_MODE: Record<string, string[]> = {
  lead_gen: DEFAULT_LEAD_GEN,
  lead_gen_basic: DEFAULT_LEAD_GEN,
  lead_gen_advanced: DEFAULT_LEAD_GEN,
  ecommerce: DEFAULT_ECOMMERCE,
  saas: DEFAULT_SAAS,
};

function normalizeMode(mode: string): string {
  return mode === "lead_gen_basic" ? "lead_gen" : mode;
}

function getAvailableMetrics(allMetrics: ReportMetric[], mode: string): ReportMetric[] {
  const normalized = normalizeMode(mode);
  return allMetrics.filter((m) => m.modes.includes("all") || m.modes.includes(normalized));
}

// ─── Component ───

export default function DashboardReportDialog({
  open,
  onOpenChange,
  totals,
  effectiveTotalLeads,
  accountName,
  dateLabel,
  dashboardMode,
  previewEnabled = false,
}: ReportDialogProps) {
  const { t } = useTranslation("dashboardReport");
  const noData = t("noData");
  const allMetrics = useMemo(() => buildAllMetrics(t, noData), [t, noData]);
  const [copied, setCopied] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    () => new Set(DEFAULTS_BY_MODE[dashboardMode] ?? DEFAULT_LEAD_GEN)
  );

  const available = useMemo(
    () => getAvailableMetrics(allMetrics, dashboardMode),
    [allMetrics, dashboardMode]
  );

  const toggleMetric = useCallback((id: string) => {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedMetrics(new Set(available.map((m) => m.id)));
  }, [available]);

  const selectDefaults = useCallback(() => {
    setSelectedMetrics(new Set(DEFAULTS_BY_MODE[dashboardMode] ?? DEFAULT_LEAD_GEN));
  }, [dashboardMode]);

  const reportText = useMemo(() => {
    const lines: string[] = [
      ...(previewEnabled ? [t("previewTag"), ""] : []),
      t("reportGreeting", { name: accountName }),
      "",
    ];

    const activeMetrics = available.filter((m) => selectedMetrics.has(m.id));
    for (const m of activeMetrics) {
      lines.push(`${m.label}: ${m.getValue(totals, effectiveTotalLeads)}`);
    }

    lines.push("", t("reportDone"), "", "•\n•\n•");
    lines.push("", t("reportPlan"), "", "•\n•\n•");

    return lines.join("\n");
  }, [totals, effectiveTotalLeads, accountName, available, selectedMetrics, previewEnabled, t]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(reportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [reportText]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0c10] border-white/[0.1] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileText className="w-5 h-5 text-[#ef3e29]" />
            {t("title", { accountName })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {previewEnabled && (
            <div className="rounded-lg border border-[#ef3e29]/25 bg-[#ef3e29]/10 px-4 py-3 text-xs text-white/75">
              {t("previewNote")}
            </div>
          )}
          {/* Metric selector */}
          <div className="border border-white/[0.08] rounded-lg overflow-hidden">
            <button
              onClick={() => setMetricsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-sm"
            >
              <span className="text-white/70">
                {t("metricsLabel", { selected: selectedMetrics.size, total: available.length })}
              </span>
              {metricsOpen ? (
                <ChevronUp className="w-4 h-4 text-white/40" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white/40" />
              )}
            </button>

            {metricsOpen && (
              <div className="border-t border-white/[0.06] p-3 space-y-2">
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={selectAll}
                    className="text-xs px-2.5 py-1 rounded bg-white/[0.06] hover:bg-white/[0.1] text-white/60 hover:text-white transition-colors"
                  >
                    {t("selectAll")}
                  </button>
                  <button
                    onClick={selectDefaults}
                    className="text-xs px-2.5 py-1 rounded bg-white/[0.06] hover:bg-white/[0.1] text-white/60 hover:text-white transition-colors"
                  >
                    {t("defaults")}
                  </button>
                  <button
                    onClick={() => setSelectedMetrics(new Set())}
                    className="text-xs px-2.5 py-1 rounded bg-white/[0.06] hover:bg-white/[0.1] text-white/60 hover:text-white transition-colors"
                  >
                    {t("deselectAll")}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {available.map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/[0.04] cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMetrics.has(m.id)}
                        onChange={() => toggleMetric(m.id)}
                        className="w-3.5 h-3.5 rounded border-white/20 bg-white/[0.06] accent-[#ef3e29] cursor-pointer"
                      />
                      <span className="text-xs text-white/80">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Report preview */}
          <textarea
            className="w-full min-h-[350px] bg-white/[0.04] border border-white/[0.1] rounded-lg p-4 text-sm text-white/90 font-mono leading-relaxed resize-y focus:outline-none focus:ring-1 focus:ring-[#ef3e29]/50"
            value={reportText}
            readOnly
          />

          <div className="flex justify-end">
            <button
              onClick={handleCopy}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                copied ? "bg-emerald-600 text-white" : "bg-[#ef3e29] hover:bg-[#d63520] text-white"
              }`}
            >
              {copied ? (
                <>
                  <ClipboardCheck className="w-4 h-4" />
                  {t("copied")}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  {t("copyReport")}
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
